import { reportError } from "@/utils/reportError";
import { trackQuizAnswered } from '@/utils/analytics';
import { fetchIsAdmin } from '@/utils/admin';
import { CHALLENGE_END, CHALLENGE_START, getDisplayToday } from '@/constants/challenge';
import i18n from '@/i18n';
import { notifyDailyProgressChanged } from '@/utils/dailyProgress';
import { supabase } from '@/utils/supabase';
import { useCallback, useEffect, useRef, useState } from 'react';

// Quiz « 12 jours » : une question par JOUR DU CHALLENGE (14 → 25 sept. 2026),
// indexée par `challenge_date` ('YYYY-MM-DD').
//
// CORRECTION CÔTÉ SERVEUR (anti-triche) : le client ne connaît JAMAIS la
// bonne réponse avant d'avoir répondu.
//   - Lecture : vue `quiz_questions_public` (question + options mélangées
//     côté serveur dans un ordre stable, SANS correct_answer ni explanation).
//   - Réponse : RPC `answer_quiz` — le serveur corrige, crédite et renvoie
//     la correction + l'explication.
//   - Jours passés répondus : RPC `reveal_quiz_answer` (correction servie
//     uniquement si une réponse existe déjà pour cette question).
// La table quiz_questions n'est plus lisible par le client, et quiz_answers
// n'est plus insérable directement (migration 20260713190000).
//
// MODE TEST (SIMULATE_CONTEST_WEEK) : le « jour affiché » vient de
// getDisplayToday() (jour du challenge de même jour de semaine). Les réponses
// sont écrites avec la DATE RÉELLE côté serveur. Le rattachement
// réponse ↔ jour du challenge se fait via question_id → challenge_date,
// jamais via answer_date. Avant le 14.09.2026, le serveur accepte de répondre
// à n'importe quel jour (période QA) ; dès le 14.09, seule la question du
// jour réel est jouable — pour tout le monde, admins compris.

export interface QuizQuestion {
  id: string;
  /** Question AFFICHÉE, localisée selon la langue courante (fallback FR). */
  question: string;
  /**
   * Options AFFICHÉES (bonne + mauvaises, mélangées côté serveur dans un
   * ordre stable). La bonne réponse n'est PAS identifiable avant de répondre.
   */
  options: string[];
  /**
   * Version CANONIQUE (française) des options, MÊME permutation que
   * `options` : options[i] et optionsFr[i] désignent la même option. Sert à
   * rapporter les textes FR renvoyés par le serveur (correction, réponse
   * choisie) vers le texte affiché.
   */
  optionsFr: string[];
  category: string | null;
  difficulty: number;
  points: number;
  /** Jour du challenge ('YYYY-MM-DD', 2026-09-14 → 2026-09-25). */
  challengeDate: string;
}

export interface QuizAnswer {
  id: string;
  questionId: string;
  answerDate: string;
  isCorrect: boolean;
  pointsEarned: number;
}

/** Correction d'une question répondue, renvoyée par le serveur. */
export interface QuizCorrection {
  /** Texte AFFICHÉ de la bonne réponse (localisé via l'index partagé). */
  correctAnswer: string;
  /** Texte AFFICHÉ de la réponse choisie (null si introuvable). */
  selectedAnswer: string | null;
  /** Explication localisée (fallback FR). */
  explanation: string | null;
}

/** Payload jsonb renvoyé par answer_quiz / reveal_quiz_answer. */
interface QuizRpcResult {
  id: string;
  question_id: string;
  answer_date: string;
  selected_answer: string | null;
  is_correct: boolean;
  points_earned: number;
  correct_answer: string;
  correct_answer_en: string | null;
  explanation: string | null;
  explanation_en: string | null;
  already_answered: boolean;
}

/** Jour de semaine (0=dim … 6=sam) d'une date ISO, calculé en UTC (aucun
 * fuseau appareil impliqué) — pour l'analytics uniquement. */
function weekdayOfDate(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Texte affiché d'une option dont on connaît la version canonique FR :
 * correspondance par index partagé options/optionsFr, fallback fourni. */
function displayOption(
  question: QuizQuestion | undefined,
  frText: string | null,
  fallback: string | null
): string | null {
  if (frText == null) return fallback;
  if (question) {
    const idx = question.optionsFr.indexOf(frText);
    if (idx >= 0) return question.options[idx];
  }
  return fallback ?? frText;
}

/** Construit la correction affichable depuis un payload RPC. */
function buildCorrection(
  question: QuizQuestion | undefined,
  payload: QuizRpcResult
): QuizCorrection {
  const useEn = i18n.language?.startsWith('en');
  return {
    correctAnswer:
      displayOption(
        question,
        payload.correct_answer,
        (useEn && payload.correct_answer_en) || payload.correct_answer
      ) ?? payload.correct_answer,
    selectedAnswer: displayOption(question, payload.selected_answer, null),
    explanation: (useEn && payload.explanation_en) || payload.explanation,
  };
}

export function useQuiz() {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  // Réponses indexées par challenge_date de la question répondue.
  const [answers, setAnswers] = useState<Map<string, QuizAnswer>>(new Map());
  // Corrections serveur (bonne réponse + explication), même indexation.
  const [corrections, setCorrections] = useState<Map<string, QuizCorrection>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Mode admin : déverrouille TOUS les jours de quiz (prévisualisation QA).
  const [isAdmin, setIsAdmin] = useState(false);
  // Garde anti double-soumission. Un double-tap rapide lit `isSubmitting`
  // (state du caller) encore à false dans les deux handlers avant le re-render
  // → deux appels RPC concurrents. Le ref est synchrone, donc fiable contre
  // la course (le serveur renverrait de toute façon already_answered).
  const submittingRef = useRef(false);

  const fetchChallengeQuiz = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setLoading(false);
        return;
      }
      setIsAdmin(await fetchIsAdmin(user.id));

      // Les 12 questions du challenge, une par jour (challenge_date UNIQUE),
      // via la RPC publique SANS correction (get_quiz_questions — ex-vue
      // quiz_questions_public, convertie en fonction pour le linter 0010 ;
      // options mélangées côté serveur, aucune colonne sensible).
      const { data: questionsData, error: questionsError } = await supabase
        .rpc('get_quiz_questions');

      if (questionsError) throw questionsError;

      const inWindow = ((questionsData ?? []) as any[])
        .filter(q => q.challenge_date >= CHALLENGE_START && q.challenge_date <= CHALLENGE_END)
        .sort((a, b) => (a.challenge_date < b.challenge_date ? -1 : 1));

      const byDate = new Map<string, QuizQuestion>();
      const byId = new Map<string, QuizQuestion>();
      inWindow.forEach(q => {
        // challenge_date est UNIQUE en base ; la Map protège quand même
        // contre un doublon inattendu (première occurrence conservée).
        if (!byDate.has(q.challenge_date)) {
          // Affichage localisé : EN si la langue courante est l'anglais ET
          // que la traduction existe (options_en non NULL, même permutation
          // que options_fr) ; sinon FR (canonique).
          const useEn = i18n.language?.startsWith('en');
          const optionsFr: string[] = q.options_fr ?? [];
          const question: QuizQuestion = {
            id: q.id,
            question: (useEn && q.question_en) || q.question,
            options: (useEn && q.options_en) || optionsFr,
            optionsFr,
            category: q.category,
            difficulty: q.difficulty,
            points: q.points,
            challengeDate: q.challenge_date,
          };
          byDate.set(q.challenge_date, question);
          byId.set(q.id, question);
        }
      });
      setQuestions(Array.from(byDate.values()));

      // Réponses de l'utilisateur, rattachées au jour du challenge via
      // question_id → challenge_date (answer_date = date réelle, inutilisable
      // en mode test). Plus de jointure quiz_questions : la table n'est plus
      // lisible par le client.
      const { data: answersData, error: answersError } = await supabase
        .from('quiz_answers')
        .select('*')
        .eq('user_id', user.id)
        .order('answer_date', { ascending: true });

      if (answersError) throw answersError;

      const answersMap = new Map<string, QuizAnswer>();
      (answersData ?? []).forEach(a => {
        const challengeDate = byId.get(a.question_id)?.challengeDate;
        if (challengeDate) {
          // Tri croissant sur answer_date → en mode test, si la même
          // question a été répondue sur deux semaines réelles, la réponse
          // la plus récente gagne.
          answersMap.set(challengeDate, {
            id: a.id,
            questionId: a.question_id,
            answerDate: a.answer_date,
            isCorrect: a.is_correct,
            pointsEarned: a.points_earned,
          });
        }
      });
      setAnswers(answersMap);

      // Corrections des questions déjà répondues (jours passés) : reveals en
      // parallèle. Un échec individuel n'invalide pas le reste.
      const answered = Array.from(answersMap.values());
      const reveals = await Promise.all(
        answered.map(async (a) => {
          const { data, error: revealError } = await supabase.rpc('reveal_quiz_answer', {
            p_question_id: a.questionId,
          });
          if (revealError || !data) return null;
          return data as QuizRpcResult;
        })
      );
      const correctionsMap = new Map<string, QuizCorrection>();
      reveals.forEach((payload) => {
        if (!payload) return;
        const q = byId.get(payload.question_id);
        if (q) correctionsMap.set(q.challengeDate, buildCorrection(q, payload));
      });
      setCorrections(correctionsMap);
    } catch (err: any) {
      reportError(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChallengeQuiz();
  }, [fetchChallengeQuiz]);

  // Changement de langue dans l'app → recharge questions ET corrections pour
  // que les textes localisés suivent immédiatement.
  useEffect(() => {
    const onLanguageChanged = () => {
      fetchChallengeQuiz();
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [fetchChallengeQuiz]);

  const submitAnswer = async (
    questionId: string,
    selectedOption: string,
    challengeDate: string
  ): Promise<{ success: boolean; isCorrect: boolean; pointsEarned: number }> => {
    if (submittingRef.current) {
      // Une soumission est déjà en cours (double-tap) — on ignore la seconde
      // pour éviter le double appel RPC et le double crédit de points.
      return { success: false, isCorrect: false, pointsEarned: 0 };
    }
    submittingRef.current = true;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return { success: false, isCorrect: false, pointsEarned: 0 };

      // Déjà répondu (état local). Points déjà crédités à la 1re réponse
      // → pointsEarned:0 pour que l'appelant ne re-crédite pas.
      if (answers.has(challengeDate)) {
        const existingAnswer = answers.get(challengeDate)!;
        return {
          success: true,
          isCorrect: existingAnswer.isCorrect,
          pointsEarned: 0,
        };
      }

      // Le SERVEUR corrige : on lui envoie le texte affiché (FR ou EN), il le
      // rapporte lui-même à la version canonique FR, recalcule is_correct et
      // points_earned (trigger + RPC), force answer_date au jour réel, et
      // renvoie la correction + l'explication.
      const { data, error: rpcError } = await supabase.rpc('answer_quiz', {
        p_question_id: questionId,
        p_selected_option: selectedOption,
      });

      if (rpcError) throw rpcError;
      const payload = data as QuizRpcResult;

      // already_answered (course résiduelle, 2e appareil, ou — en mode test —
      // une AUTRE question déjà répondue aujourd'hui) : le serveur renvoie la
      // réponse EXISTANTE, potentiellement pour une autre question. On
      // rattache via question_id, et on ne re-crédite jamais.
      const answeredQuestion = questions.find((q) => q.id === payload.question_id);
      const answeredDate = answeredQuestion?.challengeDate ?? challengeDate;

      setAnswers(prev => {
        const newMap = new Map(prev);
        newMap.set(answeredDate, {
          id: payload.id,
          questionId: payload.question_id,
          answerDate: payload.answer_date,
          isCorrect: payload.is_correct,
          pointsEarned: payload.points_earned,
        });
        return newMap;
      });
      setCorrections(prev => {
        const newMap = new Map(prev);
        newMap.set(answeredDate, buildCorrection(answeredQuestion, payload));
        return newMap;
      });
      // Pastille Missions : disparaît sans quitter l'onglet.
      notifyDailyProgressChanged("quiz");

      if (payload.already_answered) {
        // Points déjà comptés à la 1re soumission → 0 pour l'appelant.
        return { success: true, isCorrect: payload.is_correct, pointsEarned: 0 };
      }

      trackQuizAnswered(
        payload.is_correct,
        payload.points_earned,
        weekdayOfDate(challengeDate)
      );

      // Le crédit des points est fait par l'appelant (addPoints), source
      // unique pour DB + état local. Évite le double comptage.
      return {
        success: true,
        isCorrect: payload.is_correct,
        pointsEarned: payload.points_earned,
      };
    } catch (err: any) {
      reportError(err);
      setError(err.message);
      return { success: false, isCorrect: false, pointsEarned: 0 };
    } finally {
      submittingRef.current = false;
    }
  };

  /** « Aujourd'hui » dans le calendrier du challenge (mode test inclus). */
  const getTodayChallengeDate = useCallback((): string => {
    return getDisplayToday();
  }, []);

  const isQuestionUnlocked = useCallback((challengeDate: string): boolean => {
    // Les admins peuvent ouvrir n'importe quel jour pour prévisualiser.
    // (Le SERVEUR n'accepte les réponses hors jour que jusqu'au 13.09.2026.)
    if (isAdmin) return true;
    return challengeDate === getDisplayToday();
  }, [isAdmin]);

  const hasAnsweredQuestion = useCallback((challengeDate: string): boolean => {
    return answers.has(challengeDate);
  }, [answers]);

  const getAnswerForDate = useCallback((challengeDate: string): QuizAnswer | undefined => {
    return answers.get(challengeDate);
  }, [answers]);

  /** Correction serveur (bonne réponse + explication) d'un jour répondu. */
  const getCorrectionForDate = useCallback(
    (challengeDate: string): QuizCorrection | undefined => {
      return corrections.get(challengeDate);
    },
    [corrections]
  );

  return {
    questions,
    answers,
    loading,
    error,
    submitAnswer,
    getTodayChallengeDate,
    isQuestionUnlocked,
    hasAnsweredQuestion,
    getAnswerForDate,
    getCorrectionForDate,
    refetch: fetchChallengeQuiz,
  };
}
